import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, Coins, History, Award } from "lucide-react";
import { TierBadge, StatusBadge } from "@/components/Badges";
import { GrantPointsDialog } from "@/components/admin/GrantPointsDialog";
import { AwardBadgeDialog } from "@/components/admin/AwardBadgeDialog";
import { PointsUploadPanel } from "@/components/admin/PointsUploadPanel";
import { ImportLog } from "@/components/admin/ImportLog";
import { PageLoader, LoadError, EmptyState } from "@/components/admin/LoadState";
import { Pager } from "@/components/admin/Pager";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useDebounced } from "@/hooks/useDebounced";
import { fmtNum, fmtDateTime } from "@/lib/format";

// Pestañas de la sección Puntos (ver PointsHub): cada una es una página.
export function PointsGrantPage() {
  usePageTitle("Entregar puntos · Puntos");
  return <GrantToClient />;
}

export function PointsExcelPage() {
  usePageTitle("Carga masiva · Puntos");
  const [refresh, setRefresh] = useState(0);
  return (
    <div className="space-y-6">
      <PointsUploadPanel onLoaded={() => setRefresh((n) => n + 1)} />
      <ImportLog kind="puntos" refreshKey={refresh} />
    </div>
  );
}

export function PointsHistoryPage() {
  usePageTitle("Historial manual · Puntos");
  return <PointsHistory />;
}

function GrantToClient() {
  const [q, setQ] = useState("");
  const dq = useDebounced(q, 300);
  const [results, setResults] = useState<any[] | null>(null);
  const [target, setTarget] = useState<any>(null);
  const [badgeTarget, setBadgeTarget] = useState<any>(null);

  const search = () => {
    if (!dq.trim()) { setResults(null); return; }
    api.get("/admin/users/search", { params: { q: dq, tab: "approved", page_size: 8 } })
      .then((r) => setResults(r.data.items)).catch(() => setResults([]));
  };
  useEffect(search, [dq]); // eslint-disable-line

  return (
    <div className="card-obsidian p-6 space-y-4">
      <div className="relative max-w-xl">
        <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true" />
        <Input autoFocus data-testid="grant-client-search" aria-label="Buscar cliente" value={q} onChange={(e: any) => setQ(e.target.value)}
          placeholder="Busca al cliente por nombre, cédula o correo" className="pl-9 h-11 bg-obsidian border-slate-200" />
      </div>
      {results === null ? (
        <p className="text-sm text-slate-600">Escribe al menos parte del nombre, la cédula o el correo. Solo aparecen clientes con el registro aprobado.</p>
      ) : results.length === 0 ? (
        <p className="text-sm text-slate-600">No encontramos clientes aprobados para “{dq}”.</p>
      ) : (
        <ul className="divide-y divide-slate-200 border border-slate-200 rounded-lg">
          {results.map((u) => (
            <li key={u.id} className="p-3 flex flex-wrap items-center gap-3" data-testid="grant-client-result">
              <div className="flex-1 min-w-[12rem]">
                <Link to={`/admin/usuarios/${u.id}`} className="font-medium text-slate-900 hover:underline">{u.name}</Link>
                <p className="text-xs text-slate-600">Cédula {u.cedula || "—"} · {u.email}</p>
              </div>
              <TierBadge tier={u.tier} />
              <span className="font-mono text-sm text-slate-800 w-28 text-right">{fmtNum(u.points)} pts</span>
              {u.status !== "active" && <StatusBadge status={u.status} />}
              <Button size="sm" onClick={() => setTarget(u)} className="gold-gradient-bg text-white rounded-full">
                <Coins className="w-4 h-4 mr-1" aria-hidden="true" /> Entregar puntos
              </Button>
              <Button size="sm" variant="outline" onClick={() => setBadgeTarget(u)} className="rounded-full border-slate-200 text-slate-700" data-testid="grant-badge-button">
                <Award className="w-4 h-4 mr-1" aria-hidden="true" /> Insignia
              </Button>
            </li>
          ))}
        </ul>
      )}
      <GrantPointsDialog user={target} onClose={() => setTarget(null)} onDone={search} />
      <AwardBadgeDialog user={badgeTarget} onClose={() => setBadgeTarget(null)} onDone={search} />
    </div>
  );
}

function PointsHistory() {
  const [q, setQ] = useState("");
  const dq = useDebounced(q, 300);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<any>(null);
  const [failed, setFailed] = useState(false);

  const load = () => {
    setFailed(false);
    api.get("/admin/points/history", { params: { q: dq, page, page_size: 25 } }).then((r) => setData(r.data)).catch(() => setFailed(true));
  };
  useEffect(() => { setPage(1); }, [dq]);
  useEffect(load, [dq, page]); // eslint-disable-line

  return (
    <div className="space-y-4">
      <div className="relative max-w-md">
        <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" aria-hidden="true" />
        <Input aria-label="Buscar en el historial" value={q} onChange={(e: any) => setQ(e.target.value)} placeholder="Buscar por cliente, cédula o motivo" className="pl-9 h-11" />
      </div>
      {failed ? <LoadError what="el historial de entregas" onRetry={load} /> : !data ? <PageLoader /> : data.items.length === 0 ? (
        <EmptyState icon={History}>{dq ? <>No hay entregas que coincidan con “{dq}”.</> : "Todavía no se han entregado puntos desde el panel."}</EmptyState>
      ) : (
        <div className="card-obsidian overflow-hidden">
          <ul className="md:hidden divide-y divide-slate-200">
            {data.items.map((t: any) => (
              <li key={t.id} className="p-4 space-y-1" data-testid="points-history-card">
                <div className="flex items-start justify-between gap-3">
                  <Link to={`/admin/usuarios/${t.user_id}`} className="font-medium text-slate-900 hover:underline">{t.user_name}</Link>
                  <span className={`font-mono ${t.points < 0 ? "text-red-700" : "text-emerald-800"}`}>{t.points > 0 ? "+" : ""}{fmtNum(t.points)}</span>
                </div>
                <p className="text-sm text-slate-700">{t.reason || "Sin motivo"}{t.mechanism_label ? ` · ${t.mechanism_label}` : ""}</p>
                <p className="text-xs text-slate-600">{fmtDateTime(t.created_at)} · {t.created_by || "—"}</p>
              </li>
            ))}
          </ul>
          <div className="hidden md:block overflow-x-auto" tabIndex={0} role="region" aria-label="Historial de entregas de puntos">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="border-b border-slate-200 text-slate-600 text-xs uppercase tracking-wider">
                  <th scope="col" className="text-left font-medium px-4 py-3">Fecha</th>
                  <th scope="col" className="text-left font-medium px-4 py-3">Cliente</th>
                  <th scope="col" className="text-right font-medium px-4 py-3">Puntos</th>
                  <th scope="col" className="text-left font-medium px-4 py-3">Cómo</th>
                  <th scope="col" className="text-left font-medium px-4 py-3">Motivo</th>
                  <th scope="col" className="text-left font-medium px-4 py-3">Por</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((t: any) => (
                  <tr key={t.id} className="border-b border-slate-200" data-testid="points-history-row">
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtDateTime(t.created_at)}</td>
                    <td className="px-4 py-3">
                      <Link to={`/admin/usuarios/${t.user_id}`} className="text-slate-900 font-medium hover:underline">{t.user_name}</Link>
                      <p className="text-xs text-slate-600 font-mono">{t.user_cedula || "—"}</p>
                    </td>
                    <td className={`px-4 py-3 text-right font-mono ${t.points < 0 ? "text-red-700" : "text-emerald-800"}`}>{t.points > 0 ? "+" : ""}{fmtNum(t.points)}</td>
                    <td className="px-4 py-3 text-slate-700">{t.mechanism_label || "—"}</td>
                    <td className="px-4 py-3 text-slate-700 max-w-xs">{t.reason || "—"}</td>
                    <td className="px-4 py-3 text-slate-700">{t.created_by || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={data.page} pageSize={data.page_size} total={data.total} onPage={setPage} />
        </div>
      )}
    </div>
  );
}
